import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, ChevronDown, Send, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";

// Full agreement text (verbatim from /uploaded FarmToYou-Ambassador-Agreement.pdf).
// Stored in code so we can version it via `agreement_version` on the backend.
const AGREEMENT_VERSION = "2026-05-19-v1";
const AGREEMENT_SECTIONS = [
  {
    title: "Welcome to The Movement",
    body: `You're not just signing an agreement — you're joining the founding chapter of something that's changing how America eats.\n\nThe Farm To You was built on a single conviction: people deserve to know exactly where their food comes from, who raised it, and what standards it was held to. We believe premium sourcing isn't a luxury — it's a right.\n\nAnd we believe the people who carry that message into the world deserve to be recognized, rewarded, and treated like the partners they are.\n\nAs a Brand Ambassador, you are an extension of this brand. Your voice, your story, and your audience are part of how we grow — and this agreement exists to protect both of us while we build something extraordinary together.\n\nThis is a partnership built on trust, transparency, and shared values. We're glad you're here.`,
  },
  {
    title: "01 — Definitions",
    body: `"Company" refers to The Farm To You, its owners, operators, representatives, and assigns.\n"Ambassador" refers to the individual named and signing this agreement.\n"Referral" means any customer or purchase that can be directly attributed to the Ambassador through a tracked link, discount code, referral ID, or verifiable customer attribution.\n"Membership Value" means the amount a referred member actually pays for their Farm To You membership (monthly or annual), before any applicable taxes.\n"Active Ambassador" means an Ambassador who is in good standing, meeting the expectations outlined herein, and has not been terminated.`,
  },
  {
    title: "02 — Compensation & Commission",
    body: `Commission Rate. Active Ambassadors earn 10% of every membership purchased through their unique referral link, discount code, or tracked customer attribution. Because The Farm To You is a members-only marketplace, commission is a flat 10% of the membership value the referred member actually pays — with no deductions. The Company will provide transparent reporting on this calculation upon request.\n\nPayment Schedule. Commissions are calculated monthly based on completed, non-refunded memberships from the prior calendar month. Payments are issued within 15 days following the close of each monthly period via the payment method agreed upon (bank transfer, PayPal, check, or other). Minimum payout threshold: $25.00 — balances below this amount roll to the following month. Commissions on refunded memberships will be reversed in the following payout cycle.\n\nCommission Adjustments. The Company reserves the right to adjust the commission structure, rates, or payout terms in the future. Any such changes will be communicated to the Ambassador in writing no fewer than 30 days prior to taking effect. Continued participation after notice constitutes acceptance of the updated terms.`,
  },
  {
    title: "03 — Ambassador Perks & Benefits",
    body: `As an Active Ambassador of The Farm To You, you receive the following benefits at no cost for the duration of your participation:\n\n• Free membership and access to The Farm To You platform, community, and member benefits — maintained in good standing while you are an Active Ambassador.\n• Featured placement on the Brand Ambassador page of The Farm To You website, including your photo, bio, and social links.\n• Early access to new products, farm partnerships, and member-only announcements before public release.\n• Invitation to Ambassador-only events, farm visits, and community experiences as they become available.\n• A dedicated referral link and personal discount code for use with your audience.\n\nRequired Profile Submission. To be featured on the Ambassador page and activate your full perks, the Ambassador agrees to submit the following within 7 days of agreement execution: a professional headshot or high-quality photo approved for brand use; a short professional bio (100–200 words) describing who you are and why you joined; active social media profile links; and a brief personal statement or testimonial (2–4 sentences).`,
  },
  {
    title: "04 — Expectations & Standards",
    body: `Representation. Represent The Farm To You positively, professionally, and authentically in all public settings — online and offline. Promote the brand in a manner consistent with its values: premium quality, honest sourcing, and real-food standards. Engage with the brand's content, community, and mission in a consistent and genuine way.\n\nContent Standards. All promotional content must be accurate, truthful, and consistent with the brand's documented claims. Do not make false, exaggerated, or misleading claims about products, farm sourcing, health benefits, or pricing. Clearly disclose your Ambassador relationship when required by applicable law (e.g. FTC disclosures in the United States) using tags such as #ad, #sponsored, or #partner. Do not use the brand's name, logo, or imagery in a way that could embarrass, mislead, or damage the company's reputation.\n\nConduct. Maintain professional conduct in all interactions with the brand, its team, partners, and customers. Avoid public statements, content, or associations that could reasonably be considered offensive, harmful, or contrary to the values of The Farm To You. Notify the Company promptly if you become aware of any issue that could affect your ability to fulfill your role as an Ambassador.`,
  },
  {
    title: "05 — Content & Marketing Rights",
    body: `The Ambassador grants The Farm To You a non-exclusive, royalty-free, worldwide license to use, reproduce, repost, edit, and distribute any content submitted or voluntarily shared in connection with the Ambassador program. This includes submitted headshots, photos, and video content; written bios, personal statements, and testimonials; social media posts, stories, and reels that mention or tag The Farm To You; and any other promotional materials created in connection with this agreement.\n\nThe Company agrees to use such content in a manner consistent with the Ambassador's intended message and will not use content in a way that misrepresents the Ambassador's views. The Ambassador retains ownership of all original content created.\n\nThe Ambassador may not use The Farm To You's name, logo, trademarks, or branding in any commercial context outside of standard promotional activity without prior written approval.`,
  },
  {
    title: "06 — Confidentiality",
    body: `As a Brand Ambassador, you may from time to time be given access to non-public information about The Farm To You — including but not limited to: unreleased products or farm partnerships, pricing strategies, marketing plans, business operations, and member or customer data.\n\nThe Ambassador agrees to hold all such information in strict confidence and not to share, disclose, or use it for any purpose outside of their role as an Ambassador without the prior written consent of the Company.\n\nThis confidentiality obligation survives the termination of this Agreement and remains in effect indefinitely with respect to any information that remains non-public.`,
  },
  {
    title: "07 — Independent Contractor Status",
    body: `The Ambassador is an independent contractor and not an employee, agent, partner, or joint venturer of The Farm To You. Nothing in this Agreement creates any employment relationship. The Ambassador is solely responsible for any taxes, fees, or obligations arising from income earned under this Agreement.\n\nThe Ambassador has no authority to enter into contracts, make representations, or otherwise bind the Company in any way.`,
  },
  {
    title: "08 — Termination",
    body: `By Either Party. Either party may terminate this Agreement at any time, for any reason, by providing written notice to the other party. No minimum notice period is required, though we ask for professional courtesy in doing so.\n\nTermination by the Company. The Company may terminate this Agreement immediately, without notice, in any of the following circumstances: conduct that causes or risks material harm to the brand's reputation; violation of any provision of this Agreement; prolonged inactivity (90+ days without any promotional or engagement activity) without prior communication; misrepresentation of the brand, its products, or its sourcing standards; or any conduct that the Company, in its reasonable judgment, deems contrary to its values and standards.\n\nFinal Commission Payment. Upon termination, the Ambassador will receive all earned commissions for referrals completed prior to the termination date, paid according to the normal monthly payout schedule. No commissions will be earned on purchases made after the effective date of termination. Ambassador perks, including free membership and website placement, will be removed effective upon termination.`,
  },
  {
    title: "09 — Limitation of Liability",
    body: `The Company's total liability to the Ambassador under this Agreement shall not exceed the total commissions earned by the Ambassador in the three (3) months preceding any claim. In no event shall either party be liable for indirect, incidental, or consequential damages arising from this Agreement.`,
  },
  {
    title: "10 — Governing Law & Entire Agreement",
    body: `This Agreement shall be governed by and construed in accordance with the laws of the State of Ohio, without regard to its conflict of law provisions.\n\nThis Agreement constitutes the entire agreement between the parties with respect to the subject matter herein and supersedes all prior discussions, understandings, or agreements. Any modifications to this Agreement must be made in writing and signed by both parties.\n\nIf any provision of this Agreement is found to be unenforceable, the remaining provisions shall continue in full force and effect.`,
  },
];

const PRIMARY_PLATFORMS = ["Instagram", "TikTok", "YouTube", "Podcast", "Newsletter", "Other"];
const FOLLOWER_BUCKETS = ["< 1,000", "1k – 10k", "10k – 50k", "50k – 250k", "250k – 1M", "1M+"];
const PAYOUT_METHODS = ["ACH bank transfer", "PayPal", "Check by mail", "Other (let us know)"];

function AgreementCard({ section, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border border-[var(--ff-border)] rounded-xl overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[var(--ff-cream)] transition"
        data-testid={`agreement-toggle-${section.title.split(" ")[0]}`}
      >
        <span className="font-display text-base sm:text-lg text-[var(--ff-text-primary)]">{section.title}</span>
        <ChevronDown size={18} className={`text-[var(--ff-text-muted)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm leading-relaxed text-[var(--ff-text-secondary)] whitespace-pre-line border-t border-[var(--ff-border)]">
          {section.body}
        </div>
      )}
    </div>
  );
}

export default function Ambassadors() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    city_state: "",
    instagram: "",
    tiktok: "",
    youtube: "",
    website: "",
    partner_type: "Influencer / Creator",
    primary_platform: "Instagram",
    follower_count: "",
    primary_audience: "",
    why_partner: "",
    payout_method: PAYOUT_METHODS[0],
    agreement_accepted: false,
    agreement_signed_name: "",
    company: "", hp_token: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.agreement_accepted || !form.agreement_signed_name.trim()) {
      setErr("Please read the Ambassador Agreement, type your full legal name as your signature, and tick the agreement box.");
      return;
    }
    setSubmitting(true);
    try {
      // Merge primary_platform into prior_brand_experience for storage; full
      // structured collection happens in Deploy B once ambassadors collection lands.
      const payload = {
        ...form,
        partner_type: "Ambassador",
        additional_notes: `Primary platform: ${form.primary_platform}\n${form.why_partner ? "" : ""}`,
        collab_interests: ["Long-term ambassador"],
      };
      const { data } = await api.post("/partners/brand/apply", payload);
      setDone(data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white" data-testid="ambassador-success">
        <Seo title="Application received · Ambassador Program" path="/ambassadors" />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center" style={{ background: "linear-gradient(180deg, #FAF7F1, #FFFFFF)" }}>
          <CheckCircle2 size={48} className="mx-auto text-[var(--ff-primary)]" />
          <h1 className="mt-6 font-display text-4xl text-[var(--ff-text-primary)]">Welcome to the table.</h1>
          <p className="mt-4 text-[var(--ff-text-secondary)] leading-relaxed max-w-xl mx-auto">
            Thank you for applying to the Farm To You Ambassador program, <strong>{form.full_name}</strong>. We review every application personally and reach out within 5 business days when we believe there's a strong fit.
          </p>
          <p className="mt-3 text-sm text-[var(--ff-text-muted)]">
            Reference: <span className="font-mono">{done.application_id}</span>
          </p>
          <Link to="/" className="inline-flex mt-10 items-center gap-2 btn-primary" data-testid="ambassador-success-home">
            Back to home <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <Seo
        title="Ambassador Program · The Farm To You"
        description="Partner with The Farm To You. Earn 10% of every membership your audience signs up for. Your audience gets $10 off their first order. Premium American-raised beef & chicken, real ranchers, real values."
        path="/ambassadors"
      />

      {/* Hero */}
      <section className="relative text-white overflow-hidden">
        <img src="/assets/lonestar-cattle.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden />
        <div aria-hidden className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(26,67,49,0.55) 0%, rgba(26,67,49,0.40) 55%, rgba(0,0,0,0.50) 100%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-16">
          <p className="overline text-[var(--ff-citrus)]">The Farm To You · Ambassador Program</p>
          <h1 className="font-display text-5xl sm:text-7xl mt-3 leading-[1.02] max-w-3xl">Partner with The Farm To You.</h1>
          <p className="mt-6 max-w-2xl text-lg text-white/85 leading-relaxed">
            Help families discover premium American-raised beef & chicken sourced with quality, trust, and purpose. Every new customer who uses your link gets <strong>$10 off their first order</strong>, and you earn <strong>10% of every membership</strong> they sign up for through your referral — for as long as that member stays with us.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#apply" className="btn-secondary inline-flex items-center gap-2" data-testid="ambassador-cta-apply">
              Apply to become an Ambassador <ArrowRight size={16} />
            </a>
            <Link to="/login" className="text-white/85 underline underline-offset-4 hover:text-white py-3 px-2" data-testid="ambassador-cta-login">
              Ambassador login →
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits strip */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid sm:grid-cols-3 gap-8">
          {[
            { num: "10%", label: "of every membership your audience signs up for" },
            { num: "$10", label: "off the first order for every new customer you bring" },
            { num: "FREE", label: "Farm To You membership while you're an Active Ambassador" },
          ].map((b, i) => (
            <div key={i} className="card-flat p-6" data-testid={`ambassador-benefit-${i}`}>
              <p className="font-display text-5xl text-[var(--ff-primary)]">{b.num}</p>
              <p className="mt-3 text-sm text-[var(--ff-text-secondary)] leading-relaxed">{b.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[var(--ff-cream)] py-20">
        <div className="max-w-5xl mx-auto px-6">
          <p className="overline text-[var(--ff-citrus)]">How it works</p>
          <h2 className="font-display text-3xl sm:text-4xl mt-2 text-[var(--ff-text-primary)]">Five steps. No middlemen.</h2>
          <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              ["01", "Apply", "Tell us about your audience and why you connect with the brand."],
              ["02", "Get approved", "We review every application personally — usually within 5 business days."],
              ["03", "Share your link", "Receive a custom referral link and discount code for your audience."],
              ["04", "They get $10", "Your audience saves $10 on their first Farm To You box."],
              ["05", "You earn", "10% of every membership they sign up for — paid monthly."],
            ].map(([n, t, b]) => (
              <li key={n} className="space-y-2">
                <p className="overline text-[var(--ff-citrus)]">{n}</p>
                <p className="font-display text-xl text-[var(--ff-text-primary)]">{t}</p>
                <p className="text-sm text-[var(--ff-text-secondary)] leading-relaxed">{b}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Agreement */}
      <section id="agreement" className="max-w-4xl mx-auto px-6 py-20">
        <p className="overline text-[var(--ff-citrus)]">The fine print, in plain English</p>
        <h2 className="font-display text-3xl sm:text-4xl mt-2 text-[var(--ff-text-primary)]">Brand Ambassador Agreement</h2>
        <p className="mt-3 text-sm text-[var(--ff-text-muted)]">Version {AGREEMENT_VERSION} · please review before you sign</p>
        <p className="mt-4">
          <a
            href="/api/partners/ambassador/agreement.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-[var(--ff-primary)] underline underline-offset-4 hover:text-[var(--ff-primary-hover)]"
            data-testid="ambassador-agreement-download"
          >
            Download the full agreement (PDF)
            <ExternalLink size={13} />
          </a>
        </p>
        <div className="mt-8 space-y-3">
          {AGREEMENT_SECTIONS.map((s, i) => (
            <AgreementCard key={s.title} section={s} defaultOpen={i === 0} />
          ))}
        </div>
        <p className="mt-8 text-sm text-[var(--ff-text-secondary)] italic">
          Questions about the agreement? Email <a href="mailto:hello@thefarmtoyou.com" className="underline">hello@thefarmtoyou.com</a> before you sign.
        </p>
      </section>

      {/* Apply form */}
      <section id="apply" className="bg-[var(--ff-cream)] py-20">
        <div className="max-w-3xl mx-auto px-6">
          <p className="overline text-[var(--ff-citrus)]">Application</p>
          <h2 className="font-display text-3xl sm:text-4xl mt-2 text-[var(--ff-text-primary)]">Tell us about you.</h2>
          <p className="mt-3 text-sm text-[var(--ff-text-secondary)]">All fields marked with <span className="text-rose-700">*</span> are required.</p>

          <form onSubmit={submit} className="mt-10 grid sm:grid-cols-2 gap-5" data-testid="ambassador-form">
            <input className="hidden" tabIndex={-1} autoComplete="off" value={form.hp_token} onChange={(e) => u("hp_token", e.target.value)} aria-hidden />

            <Field label="Full legal name" required>
              <input required type="text" className="form-input" value={form.full_name} onChange={(e) => u("full_name", e.target.value)} data-testid="amb-full-name" />
            </Field>
            <Field label="Email" required>
              <input required type="email" className="form-input" value={form.email} onChange={(e) => u("email", e.target.value)} data-testid="amb-email" />
            </Field>
            <Field label="Phone">
              <input type="tel" className="form-input" value={form.phone} onChange={(e) => u("phone", e.target.value)} data-testid="amb-phone" />
            </Field>
            <Field label="City, State" required>
              <input required type="text" className="form-input" placeholder="e.g. Miami, FL" value={form.city_state} onChange={(e) => u("city_state", e.target.value)} data-testid="amb-city-state" />
            </Field>

            <Field label="Instagram handle">
              <input type="text" className="form-input" placeholder="@you" value={form.instagram} onChange={(e) => u("instagram", e.target.value)} data-testid="amb-instagram" />
            </Field>
            <Field label="TikTok handle">
              <input type="text" className="form-input" placeholder="@you" value={form.tiktok} onChange={(e) => u("tiktok", e.target.value)} data-testid="amb-tiktok" />
            </Field>
            <Field label="YouTube channel URL">
              <input type="url" className="form-input" value={form.youtube} onChange={(e) => u("youtube", e.target.value)} data-testid="amb-youtube" />
            </Field>
            <Field label="Personal / brand website">
              <input type="url" className="form-input" value={form.website} onChange={(e) => u("website", e.target.value)} data-testid="amb-website" />
            </Field>

            <Field label="Primary platform" required>
              <select className="form-input" value={form.primary_platform} onChange={(e) => u("primary_platform", e.target.value)} data-testid="amb-primary-platform">
                {PRIMARY_PLATFORMS.map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Total followers across platforms">
              <select className="form-input" value={form.follower_count} onChange={(e) => u("follower_count", e.target.value)} data-testid="amb-followers">
                <option value="">Select…</option>
                {FOLLOWER_BUCKETS.map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>

            <Field label="Audience location" full>
              <input type="text" className="form-input" placeholder="e.g. South Florida, NYC; ages 25-45; foodies & fitness" value={form.primary_audience} onChange={(e) => u("primary_audience", e.target.value)} data-testid="amb-audience" />
            </Field>

            <Field label="Why do you want to partner with The Farm To You?" full required>
              <textarea required rows={4} className="form-input resize-none" placeholder="Tell us in 2–4 sentences what draws you to the brand and how you'd talk about it." value={form.why_partner} onChange={(e) => u("why_partner", e.target.value)} data-testid="amb-why" />
            </Field>

            <Field label="Preferred payout method" full required>
              <select required className="form-input" value={form.payout_method} onChange={(e) => u("payout_method", e.target.value)} data-testid="amb-payout-method">
                {PAYOUT_METHODS.map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>

            {/* Agreement gate */}
            <div className="sm:col-span-2 mt-2 p-5 rounded-xl bg-white border border-[var(--ff-border)]">
              <p className="text-sm font-medium text-[var(--ff-text-primary)]">Agreement</p>
              <p className="mt-2 text-sm text-[var(--ff-text-secondary)]">
                By typing your full legal name and ticking the box below, you confirm you have read the entire{" "}
                <a href="#agreement" className="underline">Brand Ambassador Agreement</a> ({AGREEMENT_VERSION}) and agree to be bound by its terms.
                Approval is at the sole discretion of The Farm To You. Commissions are subject to order completion, refunds, chargebacks, and admin approval.
              </p>
              <div className="mt-4">
                <Field label="Type your full legal name as your signature" required>
                  <input required type="text" className="form-input" value={form.agreement_signed_name} onChange={(e) => u("agreement_signed_name", e.target.value)} data-testid="amb-signature" />
                </Field>
              </div>
              <label className="mt-3 flex items-start gap-3 cursor-pointer text-sm text-[var(--ff-text-secondary)]" data-testid="amb-agreement-label">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.agreement_accepted}
                  onChange={(e) => u("agreement_accepted", e.target.checked)}
                  data-testid="amb-agreement-checkbox"
                  required
                />
                <span>
                  I have read and agree to the Brand Ambassador Agreement and acknowledge that I am an independent contractor responsible for my own taxes on commissions earned.
                </span>
              </label>
            </div>

            {err && (
              <div className="sm:col-span-2 text-sm text-rose-700" data-testid="ambassador-error">{err}</div>
            )}

            <div className="sm:col-span-2 mt-2">
              <button type="submit" disabled={submitting} className="btn-primary inline-flex items-center gap-2" data-testid="ambassador-submit">
                {submitting ? "Submitting…" : <>Submit application <Send size={16} /></>}
              </button>
              <p className="mt-3 text-xs text-[var(--ff-text-muted)]">
                Already an ambassador? <Link to="/login" className="underline">Sign in to your dashboard <ExternalLink size={11} className="inline" /></Link>
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, full, children }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-sm font-medium text-[var(--ff-text-primary)]">
        {label} {required && <span className="text-rose-700">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
